import React from 'react';
import Select from 'react-select';

import './index.css';
import { countQualifyingFilters, MIN_QUALIFYING_FILTERS, SENIORITY_OPTIONS, EMPLOYEE_COUNT_OPTIONS } from './filterUtils';

const LeadsFilters = ({ filters, filterOptions, optionsLoading, onChange, onApply, onClear, onSearchChange }) => {
  const handleText = (key) => (e) => onChange({ [key]: e['target']['value'] });
  const handleCheck = (key) => (e) => onChange({ [key]: e['target']['checked'] });
  const handleMulti = (key) => (opts) => onChange({ [key]: (opts || []).map(o => o['value']) });

  const toOptions = (list) => (list || []).map(({ value }) => ({ value, label: value }));
  // Reason: tolerate legacy single-string values (old URLs / replayed Milo results) alongside the array shape
  const toValue = (key, options) => {
    const raw = filters[key];
    const values = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    return values.map(v => (options || []).find(o => o['value'] === v) || { value: v, label: v });
  };

  const dataDriven = [
    { key: 'department', label: 'Department', options: toOptions(filterOptions?.['departments']) },
    { key: 'industry', label: 'Industry', options: toOptions(filterOptions?.['industries']) },
    { key: 'revenue', label: 'Revenue', options: toOptions(filterOptions?.['revenues']) },
  ];

  const selectedCountries = Array.isArray(filters['country']) ? filters['country'] : (filters['country'] ? [filters['country']] : []);
  const selectedStates = Array.isArray(filters['state']) ? filters['state'] : (filters['state'] ? [filters['state']] : []);
  // States are US-only data — show the State filter only for US searches (or while a state is still set, so it can be removed)
  const showStateFilter = selectedCountries.includes('United States') || selectedStates.length > 0;

  // Deselecting United States clears any picked states so no hidden filter stays applied
  const handleCountryChange = (opts) => {
    const countries = (opts || []).map(o => o['value']);
    onChange(countries.includes('United States') ? { country: countries } : { country: countries, state: [] });
  };

  const multiSelect = (key, label, options, placeholder) => (
    <div className="leads-filters-field" key={key}>
      <span className="leads-filters-label">{label}</span>
      <Select
        isMulti
        isClearable
        isLoading={optionsLoading}
        placeholder={placeholder || `Any ${label.toLowerCase()}`}
        value={toValue(key, options)}
        onChange={handleMulti(key)}
        options={options}
        classNamePrefix="react-select"
      />
    </div>
  );

  const textField = (key, label, placeholder) => (
    <div className="leads-filters-field" key={key}>
      <span className="leads-filters-label">{label}</span>
      <input
        className="leads-filters-search-input"
        type="text"
        placeholder={placeholder}
        value={filters[key] || ''}
        onChange={handleText(key)}
      />
    </div>
  );

  const appliedCount = countQualifyingFilters(filters);
  const canApply = appliedCount >= MIN_QUALIFYING_FILTERS;

  return (
    <div className="leads-filters-panel">
      <div className="leads-filters-title">Filters</div>

      <div className="leads-filters-field">
        <span className="leads-filters-label">Keyword</span>
        <input
          className="leads-filters-search-input"
          type="text"
          placeholder="Name, company, technology, ZIP…"
          value={filters['q'] || ''}
          onChange={onSearchChange}
        />
      </div>

      {textField('jobTitle', 'Job Title', 'e.g. Marketing Director')}
      {multiSelect('seniority', 'Seniority', SENIORITY_OPTIONS, 'Any level')}
      {dataDriven.map(({ key, label, options }) => multiSelect(key, label, options))}

      <div className="leads-filters-field">
        <span className="leads-filters-label">Country</span>
        <Select
          isMulti
          isClearable
          isLoading={optionsLoading}
          placeholder="Any country"
          value={toValue('country', toOptions(filterOptions?.['countries']))}
          onChange={handleCountryChange}
          options={toOptions(filterOptions?.['countries'])}
          classNamePrefix="react-select"
        />
      </div>
      {showStateFilter && multiSelect('state', 'State (US)', toOptions(filterOptions?.['states']), 'Any state')}

      {multiSelect('employeeCount', 'Company Size', EMPLOYEE_COUNT_OPTIONS, 'Any size')}
      {textField('city', 'City', 'e.g. Austin')}

      <div className="leads-filters-field">
        <span className="leads-filters-label">Contact Info</span>
        <div className="leads-filters-checkbox-group">
          <label className="leads-filters-checkbox">
            <input type="checkbox" checked={filters['hasEmail']} onChange={handleCheck('hasEmail')} />
            Verified Email
          </label>
          <label className="leads-filters-checkbox">
            <input type="checkbox" checked={filters['hasPhone']} onChange={handleCheck('hasPhone')} />
            Direct Dial
          </label>
          <label className="leads-filters-checkbox">
            <input type="checkbox" checked={filters['hasLinkedin']} onChange={handleCheck('hasLinkedin')} />
            LinkedIn Profile
          </label>
        </div>
      </div>

      <div className="leads-filters-actions">
        <button className="btn-style-new leads-filters-apply-btn" onClick={onApply} disabled={!canApply}>Apply Filters</button>
        <button className="btn-style-border leads-filters-clear-btn" onClick={onClear}>Clear</button>
      </div>
    </div>
  );
};

export default LeadsFilters;
